import { useState, useRef, useEffect } from "react";
import Indentification from "../../components/elements/Indentification";
import { initialsValue } from "../../helpers";
import {
  backendSeen,
  notificationList,
} from "../../services/communicationService";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const NotificationSection = () => {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const scrollRef = useRef(null);
  const queryClient = useQueryClient();

  const limit = 10;
  const navigate = useNavigate();
  const { session } = useSelector((state) => state?.authProvider);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: ["notifications"],
      queryFn: async ({ pageParam = 1 }) => {
        const res = await notificationList({
          limit,
          page: pageParam,
          sortQuery: "-createdAt",
        });

        if (res?.docs?.status !== 200) {
          return { data: [], metadata: {} };
        }

        return res.docs;
      },
      getNextPageParam: (lastPage) => {
        return lastPage?.metadata?.hasNextPage
          ? lastPage?.metadata?.nextPage
          : undefined;
      },
    });
  const handleNotificationClick = async (id) => {
    const role = session?.user?.role?.toLowerCase();

    try {
      // Call seen API
      await backendSeen({ id }); // or { notificationId: id } depending on backend
      await queryClient.invalidateQueries(["notifications"]);
    } catch (error) {
      console.error("Seen API failed", error);
    }

    setIsNotificationOpen(false);

    if (["admin", "recruiter"].includes(role)) {
      navigate("/admin/job-applications");
    } else if (role === "user") {
      navigate("/saved-jobs");
    }
  };
  const notificationCount = data?.pages?.[0]?.unseenCount || 0;

  const allNotifications =
    data?.pages?.flatMap((page) => page?.data || []) || [];

  const notifications = allNotifications.map((item) => ({
    id: item?._id,
    content: item?.content,
    user: {
      name: `${item?.userData?.firstName || ""} ${
        item?.userData?.lastName || ""
      }`,
      image: item?.userData?.image?.url || null,
    },
    timestamp: item?.createdAt,
    isRead: item?.seenStatus,
  }));
  useEffect(() => {
    const el = scrollRef.current;

    if (!el || !isNotificationOpen) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;

      if (
        scrollTop + clientHeight >= scrollHeight - 5 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    };

    el.addEventListener("scroll", handleScroll);

    return () => el.removeEventListener("scroll", handleScroll);
  }, [isNotificationOpen, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const wrapperRef = useRef(null);
  const toggleNotifications = () => setIsNotificationOpen((prev) => !prev);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  return (
    <div
      ref={wrapperRef}
      className="mb-2 min-w-[4.5rem] rounded-xl bg-black/20 transition-all duration-300 ease-in-out relative"
    >
      {/* Notification Header */}
      <div
        className="flex cursor-pointer items-center justify-between gap-3 p-3 text-white transition-all duration-300 ease-in-out relative hover:bg-white/10 rounded-xl"
        onClick={toggleNotifications}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <i className="fa-solid fa-bell text-xl" />
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-orange-500 px-1.5 text-xs font-bold text-white">
                {notificationCount > 99 ? "99+" : notificationCount}
              </span>
            )}
          </div>
          <span className="text-sm font-medium">Notifications</span>
        </div>
        <i
          className={`fa-solid fa-chevron-${isNotificationOpen ? "up" : "down"} text-xs`}
        />
      </div>

      {/* Notification Dropdown */}
      {isNotificationOpen && (
        <div className="px-3 pb-3 absolute bottom-0 left-full z-10 shadow-lg rounded-lg bg-white/90 backdrop-blur-sm ">
          <div
            ref={scrollRef}
            className="max-h-[400px] min-w-80 overflow-y-auto scrollbar"
          >
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-black">
                <i className="fa-regular fa-bell-slash text-3xl mb-2" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-black/10">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification.id)}
                    className={`flex items-start gap-3 p-3 transition-all duration-200 cursor-pointer group relative
    ${
      notification.isRead
        ? "bg-transparent hover:bg-black/5"
        : "bg-blue-50 border-l-4 border-blue-500 hover:bg-blue-100"
    }
  `}
                  >
                    {/* User Avatar or Icon */}
                    <div className="flex-shrink-0">
                      {notification.type === "info" ? (
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/20">
                          <i className="fa-solid fa-info text-blue-400" />
                        </div>
                      ) : notification.user.image ? (
                        <Indentification
                          image={notification.user.image}
                          alt={notification.user.name}
                          initial={initialsValue(notification.user.name)}
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-black font-semibold text-sm">
                          {initialsValue(notification.user.name)}
                        </div>
                      )}
                    </div>

                    {/* Notification Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        {/* <h4 className="text-sm font-semibold text-black">
                          Notification
                        </h4> */}
                        {/* {!notification.isRead && (
                          <span className="flex h-2 w-2 flex-shrink-0 rounded-full bg-orange-500 mt-1">
                            <span className="absolute inline-flex h-2 w-2 rounded-full bg-orange-500 opacity-75 animate-ping"></span>
                          </span>
                        )} */}
                      </div>
                      {/* <p className="mt-0.5 text-sm text-black/80">
                        <span className="font-medium">
                          {notification.user.name}
                        </span>{" "}
                        {notification.message}
                      </p> */}
                      <p
                        className={`mt-0.5 text-sm ${
                          notification.isRead
                            ? "text-black/70"
                            : "text-black font-semibold"
                        }`}
                        dangerouslySetInnerHTML={{
                          __html: notification.content,
                        }}
                      />

                      <p className="mt-1 text-xs text-black/50">
                        {dayjs(notification.timestamp).format(
                          "DD MMM, YYYY hh:mm A",
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {isFetchingNextPage && (
            <div className="p-3 text-center text-sm text-gray-500">
              Loading more...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationSection;
